/**
 * MUI Theme Configuration
 * Uses design tokens from ./tokens.js
 */
import { createTheme } from "@mui/material";
import { FONT, COLORS, LIGHT, DARK } from "./tokens";

export const buildMuiTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: mode === "dark"
      ? { main: COLORS.accent, dark: COLORS.accentHover, light: COLORS.accentLight }
      : { main: "#B45309", dark: "#92400E", light: "#D97706" },
    secondary: { main: mode === "dark" ? COLORS.success : "#16A34A" },
    error: { main: mode === "dark" ? COLORS.error : "#DC2626" },
    // No warning color, this project uses green/red only
    background: mode === "dark"
      ? { default: DARK.bg, paper: DARK.surface1 }
      : { default: LIGHT.bg, paper: LIGHT.surface0 },
    text: mode === "dark"
      ? { primary: DARK.text1, secondary: DARK.text2, disabled: DARK.text3 }
      : { primary: LIGHT.text1, secondary: LIGHT.text2, disabled: LIGHT.text3 },
    divider: mode === "dark" ? DARK.border : LIGHT.border,
  },
  typography: { fontFamily: FONT, fontSize: 13 },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
          letterSpacing: "-.01em",
        },
        containedPrimary: mode === "dark" ? {
          background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentHover} 100%)`,
          boxShadow: `0 2px 12px rgba(75,127,250,.3)`,
          "&:hover": {
            background: `linear-gradient(135deg, ${COLORS.accentLight} 0%, ${COLORS.accent} 100%)`,
            boxShadow: `0 4px 20px rgba(75,127,250,.4)`,
          },
        } : {
          background: `linear-gradient(135deg, #D97706 0%, #B45309 100%)`,
          boxShadow: `0 1px 4px rgba(180,83,9,.25)`,
          "&:hover": {
            background: `linear-gradient(135deg, #F59E0B 0%, #D97706 100%)`,
            boxShadow: `0 4px 12px rgba(180,83,9,.3)`,
          },
        },
        outlinedPrimary: mode === "dark" ? {
          borderColor: DARK.border,
          color: DARK.text2,
          "&:hover": {
            borderColor: `rgba(75,127,250,.5)`,
            background: `rgba(75,127,250,.06)`,
            color: COLORS.accent,
          },
        } : {
          borderColor: LIGHT.border,
          color: LIGHT.text2,
          "&:hover": {
            borderColor: `#B45309`,
            background: `rgba(180,83,9,.06)`,
            color: `#B45309`,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          ...(mode === "light" && {
            color: LIGHT.text2,
            "&.Mui-selected": { color: "#B45309" },
          }),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: mode === "dark" ? COLORS.accent : "#B45309",
          height: 2,
          borderRadius: "2px 2px 0 0",
          boxShadow: mode === "dark"
            ? `0 -2px 8px rgba(75,127,250,.4)`
            : `0 -2px 8px rgba(180,83,9,.3)`,
        },
        ...(mode === "light" && {
          root: { borderBottom: `1px solid ${LIGHT.border}` },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: 12,
          ...(mode === "dark" && { borderColor: DARK.border }),
          ...(mode === "light" && {
            borderColor: LIGHT.border,
            backgroundColor: LIGHT.surface2,
            color: LIGHT.text2,
          }),
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: 12,
          ...(mode === "dark" && {
            background: `linear-gradient(135deg, rgba(255,255,255,.025) 0%, rgba(255,255,255,.005) 60%, transparent 100%), ${DARK.surface1}`,
            border: `1px solid ${DARK.border}`,
            boxShadow: `0 2px 12px rgba(0,0,0,.65), 0 1px 0 rgba(255,255,255,.03)`,
          }),
          ...(mode === "light" && {
            background: LIGHT.surface0,
            border: `1px solid ${LIGHT.border}`,
            boxShadow: LIGHT.shadow,
          }),
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: 12,
          ...(mode === "dark" && {
            backgroundColor: DARK.surface1,
            border: `1px solid ${DARK.border}`,
            boxShadow: `0 2px 12px rgba(0,0,0,.65), 0 1px 0 rgba(255,255,255,.03)`,
          }),
          ...(mode === "light" && {
            backgroundColor: LIGHT.surface0,
            boxShadow: LIGHT.shadow,
          }),
        },
        outlined: mode === "dark" ? {
          borderColor: DARK.border,
        } : {
          borderColor: LIGHT.border,
          boxShadow: "none",
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small" },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: mode === "dark" ? {
          backgroundColor: DARK.surface2,
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: DARK.border,
            borderWidth: "1px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: DARK.borderStrong,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: COLORS.accent,
            borderWidth: "1px",
            boxShadow: `0 0 0 3px rgba(75,127,250,.18)`,
          },
        } : {
          backgroundColor: LIGHT.surface0,
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: LIGHT.border,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: LIGHT.borderStrong,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#B45309",
            boxShadow: `0 0 0 3px rgba(180,83,9,.12)`,
          },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        // Backdrop blur adds depth to the dialog
        BackdropProps: { style: { backdropFilter: "blur(6px)", backgroundColor: "rgba(0,0,0,.6)" } },
      },
      styleOverrides: {
        paper: {
          backgroundImage: "none",
          borderRadius: 16,
          ...(mode === "dark" && {
            backgroundColor: DARK.surface1,
            border: `1px solid ${DARK.borderStrong}`,
            boxShadow: `0 32px 80px rgba(0,0,0,.9), 0 0 0 1px rgba(75,127,250,.10)`,
          }),
          ...(mode === "light" && {
            backgroundColor: LIGHT.surface0,
            border: `1px solid ${LIGHT.border}`,
            boxShadow: LIGHT.shadowXl,
          }),
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: mode === "dark" ? {
          borderBottom: `1px solid ${DARK.borderSub}`,
        } : {
          borderBottom: `1px solid ${LIGHT.border}`,
          color: LIGHT.text1,
        },
        head: mode === "dark" ? {
          color: DARK.text2,
          fontWeight: 600,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: ".06em",
        } : {
          color: LIGHT.text3,
          fontWeight: 600,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: ".06em",
          backgroundColor: LIGHT.surface1,
          borderBottom: `1px solid ${LIGHT.border}`,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: mode === "dark" ? {
          backgroundColor: DARK.surface2,
          borderRadius: 4,
        } : {
          backgroundColor: LIGHT.surface3,
          borderRadius: 4,
        },
        bar: {
          backgroundColor: COLORS.accent,
          borderRadius: 4,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 6,
          padding: "6px 10px",
          ...(mode === "dark" && {
            background: DARK.surface3,
            border: `1px solid ${DARK.borderStrong}`,
            boxShadow: `0 8px 32px rgba(0,0,0,.75)`,
            color: DARK.text1,
          }),
          ...(mode === "light" && {
            background: LIGHT.text1,
            color: "#FFFFFF",
            boxShadow: `0 4px 12px rgba(0,0,0,.15)`,
          }),
        },
        arrow: mode === "dark" ? {
          color: DARK.surface3,
        } : {
          color: LIGHT.text1,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: mode === "dark" ? {
          borderColor: DARK.border,
        } : {
          borderColor: LIGHT.border,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: mode === "dark" ? {
          borderColor: DARK.borderSub,
        } : {
          borderColor: LIGHT.borderSub,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: mode === "light" ? {
          borderRadius: 8,
          fontSize: 13,
        } : {},
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: mode === "light" ? {
          color: LIGHT.text3,
          "&.Mui-focused": { color: "#B45309" },
        } : {},
      },
    },
  },
});
